import React, { useEffect, useMemo, useRef, useState } from 'react';
import { canEditMissionBoard, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';
import { liveCountForMission } from '../utils/dashboard/missionsModel';
import { PRESENCE_STATUS } from '../utils/dashboard/constants';
import { summarizeFreqModes } from './MemberCollapsedCard';
import {
  loadTrackVisualPrefs,
  saveTrackVisualPrefs,
  DEFAULT_TRACK_VISUAL_PREFS
} from '../utils/dashboard/trackVisualPrefs';

/**
 * 当前任务控制条（任务中页顶）：
 * - 点名称区空白 → 下拉切换其它任务；双击名称改名
 * - 同行右：显示设置 + ···（回放 / 导出KML / 任务设置）
 * - 名称下：执行中 · 参与 · 在线；再下一行模式汇总条
 */
export default function MissionTabsBar({
  missions = [],
  activeMissionId = null,
  liveMembers = [],
  getMemberPresence = null,
  onSelectMission,
  onCreateMission,
  onRenameMission,
  onCloseMission,
  onToggleMissionRecordTracks,
  onOpenMissionPlayback,
  onExportMissionKml = null,
  canPlayback = true,
  participantCount = null,
  onlineCount = null
}) {
  const list = Array.isArray(missions) ? missions : [];
  const active = list.find((m) => m.id === activeMissionId) || list[0] || null;
  const others = active ? list.filter((m) => m.id !== active.id) : list;
  const canEditBoard = canEditMissionBoard();
  const canRecordTracks = canEditBoard && hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [switchOpen, setSwitchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [taskSettingsOpen, setTaskSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => loadTrackVisualPrefs());
  const rootRef = useRef(null);

  useEffect(() => {
    if (editingId && !list.some((m) => m.id === editingId)) {
      setEditingId(null);
      setDraft('');
    }
  }, [list, editingId]);

  useEffect(() => {
    if (!switchOpen && !moreOpen && !settingsOpen && !taskSettingsOpen) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains?.(e.target)) return;
      setSwitchOpen(false);
      setMoreOpen(false);
      setSettingsOpen(false);
      setTaskSettingsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSwitchOpen(false);
        setMoreOpen(false);
        setSettingsOpen(false);
        setTaskSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [switchOpen, moreOpen, settingsOpen, taskSettingsOpen]);

  const beginEdit = (m) => {
    if (!m) return;
    setSwitchOpen(false);
    setEditingId(m.id);
    setDraft(String(m.name || ''));
  };

  const commitEdit = () => {
    if (!editingId) return;
    const name = String(draft || '').trim();
    if (name) onRenameMission?.(editingId, name);
    setEditingId(null);
    setDraft('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const editing = active && editingId === active.id;

  const resolvedParticipant = participantCount != null
    ? participantCount
    : (active ? liveCountForMission(active, liveMembers) : 0);

  const resolvedOnline = useMemo(() => {
    if (onlineCount != null) return onlineCount;
    if (!active) return 0;
    const ids = new Set((active.memberIds || []).map((x) => String(x || '').trim()).filter(Boolean));
    if (!ids.size) return 0;
    let n = 0;
    (liveMembers || []).forEach((m) => {
      const key = String(m?.device_unique_id || m?.name || '').trim();
      const hit = ids.has(key)
        || (active.memberIds || []).some((id) => String(id) === key || String(id) === String(m?.name || ''));
      if (!hit) return;
      let st = PRESENCE_STATUS.OFFLINE;
      if (typeof getMemberPresence === 'function') {
        st = getMemberPresence(m) || st;
      }
      if (st === PRESENCE_STATUS.ONLINE || st === 'online') n += 1;
    });
    return n;
  }, [onlineCount, active, liveMembers, getMemberPresence]);

  const offlineCount = useMemo(() => {
    if (!active) return 0;
    const total = resolvedParticipant;
    return Math.max(0, total - resolvedOnline);
  }, [active, resolvedParticipant, resolvedOnline]);


  const freqTallies = useMemo(() => {
    if (!active) return { boost: 0, pending: 0, eco: 0, offline: offlineCount };
    const ids = new Set((active.memberIds || []).map((x) => String(x || '').trim()).filter(Boolean));
    const members = (liveMembers || []).filter((m) => {
      const key = String(m?.device_unique_id || m?.name || '').trim();
      return ids.has(key) || (active.memberIds || []).some((id) => String(id) === key || String(id) === String(m?.name || ''));
    });
    const tallies = summarizeFreqModes(members, getMemberPresence);
    // 无策略字段时至少保证离线数正确
    if (tallies.boost + tallies.pending + tallies.eco + tallies.offline === 0) {
      return { boost: 0, pending: 0, eco: 0, offline: offlineCount };
    }
    // 若策略统计的离线偏少，用在线判定离线兜底
    if (tallies.offline === 0 && offlineCount > 0 && tallies.boost + tallies.pending + tallies.eco < resolvedParticipant) {
      return { ...tallies, offline: offlineCount };
    }
    return tallies;
  }, [active, liveMembers, getMemberPresence, offlineCount, resolvedParticipant]);

  const updatePref = (patch) => {
    const next = saveTrackVisualPrefs({ ...prefs, ...patch });
    setPrefs(next);
  };

  const menuItemStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    padding: '8px 12px',
    fontSize: 13,
    color: '#37474f',
    cursor: 'pointer',
    borderRadius: 6
  };

  return (
    <div
      ref={rootRef} className="mission-task-header"
      style={{
        marginTop: 4,
        marginBottom: 8,
        padding: '8px 4px 0',
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        position: 'relative'
      }}
    >
      {/* 名称行 */}
      <div className="mission-task-title-row" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div
          aria-hidden
          title="任务"
          style={{
            width: 22,
            height: 22,
            flexShrink: 0,
            color: '#9ca3af',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M3 18L9 8l3.5 5.5L15 10l6 8H3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M14 18l2.2-3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </div>

        {active ? (
          <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
            <div
              role="button"
              tabIndex={0}
              aria-haspopup="listbox"
              aria-expanded={switchOpen}
              onClick={(e) => {
                if (editing) return;
                if (e.target.closest?.('[data-mission-interactive="1"]')) return;
                setMoreOpen(false);
                setSettingsOpen(false);
                setTaskSettingsOpen(false);
                setSwitchOpen((v) => !v);
              }}
              onKeyDown={(e) => {
                if (editing) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSwitchOpen((v) => !v);
                }
              }}
              title="点空白处切换其它任务；双击名称改名"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: '100%',
                height: 34,
                padding: '0 4px',
                borderRadius: 8,
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                color: '#111827',
                boxShadow: 'none',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1.2,
                userSelect: 'none'
              }}
            >
              {editing ? (
                <input
                  data-mission-interactive="1"
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEdit();
                    }
                  }}
                  onBlur={commitEdit}
                  style={{
                    width: Math.min(160, Math.max(72, (draft || '').length * 12 + 24)),
                    height: 24,
                    border: '1px solid #90caf9',
                    borderRadius: 4,
                    padding: '0 6px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#0d47a1'
                  }}
                />
              ) : (
                <span
                  data-mission-interactive="1"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    beginEdit(active);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  title="双击修改任务名称"
                  style={{
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#111827',
                    fontSize: 16,
                    fontWeight: 700
                  }}
                >
                  {active.name || '任务'}
                </span>
              )}
              <span
                aria-hidden
                style={{
                  fontSize: 10,
                  opacity: 0.85,
                  transform: switchOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s ease'
                }}
              >
                {'\u25BE'}
              </span>
            </div>

            {switchOpen && (
              <div
                role="listbox"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  minWidth: 220,
                  maxWidth: 320,
                  maxHeight: 280,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #c5d8f5',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(13,71,161,0.18)',
                  zIndex: 50,
                  padding: 6
                }}
              >
                {others.length === 0 ? (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: '#8a93a0' }}>
                    暂无其它任务
                  </div>
                ) : (
                  others.map((m) => (
                    <div
                      key={m.id}
                      role="option"
                      onClick={() => {
                        onSelectMission?.(m.id);
                        setSwitchOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#37474f',
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#eef5ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      >
                        {m.name || '任务'}
                      </span>
                      <span style={{
                        minWidth: 18,
                        height: 18,
                        padding: '0 5px',
                        borderRadius: 9,
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#e3f2fd',
                        color: '#1565c0'
                      }}
                      >
                        {liveCountForMission(m, liveMembers)}
                      </span>
                      {canEditBoard && (<button
                        type="button"
                        title="关闭此任务"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCloseMission?.(m.id);
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#90a4ae',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 14,
                          lineHeight: 1,
                          fontWeight: 700
                        }}
                      >
                        {'\u00d7'}
                      </button>)}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: '#8a93a0', flex: 1 }}>暂无任务</span>
        )}

        {canEditBoard && (<button
          type="button"
          onClick={() => onCreateMission?.()}
          style={{
            height: 32,
            padding: '0 10px',
            borderRadius: 8,
            border: '1px dashed #1565c0',
            background: '#fff',
            color: '#1565c0',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          + 新建
        </button>)}

        {active ? (
          <>
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                type="button"
                data-mission-interactive="1"
                title="显示设置"
                onClick={(e) => {
                  e.stopPropagation();
                  setSwitchOpen(false);
                  setMoreOpen(false);
                  setTaskSettingsOpen(false);
                  setSettingsOpen((v) => !v);
                }}
                style={{
                  height: 30,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: settingsOpen ? '1px solid #93c5fd' : '1px solid transparent',
                  background: settingsOpen ? '#eff6ff' : 'transparent',
                  color: '#4b5563',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {'\u2699'} 显示设置
              </button>
              {settingsOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    width: 280,
                    background: '#fff',
                    border: '1px solid #c5d8f5',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(13,71,161,0.18)',
                    zIndex: 55,
                    padding: 12
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0d47a1', marginBottom: 10 }}>
                    队员轨迹线 · 显示设置
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, color: '#455a64' }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>轨迹线宽</span>
                      <select
                        value={prefs.lineWidth}
                        onChange={(e) => updatePref({ lineWidth: Number(e.target.value) })}
                        style={{ height: 28, borderRadius: 6, border: '1px solid #d0d7de', padding: '0 8px' }}
                      >
                        {[2, 3, 4, 5, 6, 8].map((w) => (
                          <option key={w} value={w}>{w}px</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>大空隙虚线断段</span>
                      <input
                        type="checkbox"
                        checked={prefs.gapDashed !== false}
                        onChange={(e) => updatePref({ gapDashed: e.target.checked })}
                      />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>按队员分色</span>
                      <input
                        type="checkbox"
                        checked={prefs.colorByMember !== false}
                        onChange={(e) => updatePref({ colorByMember: e.target.checked })}
                      />
                    </label>
                    <div style={{ fontSize: 11, color: '#8a93a0', lineHeight: 1.45 }}>
                      离线汇总按指挥台在线判定；线宽/断段对新绘制与刷新后的轨迹生效。
                      默认线宽 {DEFAULT_TRACK_VISUAL_PREFS.lineWidth}px。
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>任务记录轨迹</span>
                      <input
                        type="checkbox"
                        checked={active.recordTracks === true}
                        disabled={!canRecordTracks}
                        onChange={(e) => canRecordTracks && onToggleMissionRecordTracks?.(active.id, e.target.checked)}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <button
                type="button"
                data-mission-interactive="1"
                title="更多"
                onClick={(e) => {
                  e.stopPropagation();
                  setSwitchOpen(false);
                  setSettingsOpen(false);
                  setTaskSettingsOpen(false);
                  setMoreOpen((v) => !v);
                }}
                style={{
                  height: 32,
                  minWidth: 32,
                  padding: '0 8px',
                  borderRadius: 8,
                  border: moreOpen ? '1px solid #1565c0' : '1px solid #c5d8f5',
                  background: moreOpen ? '#e3f2fd' : '#fff',
                  color: '#455a64',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                ···
              </button>
              {moreOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    minWidth: 168,
                    background: '#fff',
                    border: '1px solid #c5d8f5',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(13,71,161,0.18)',
                    zIndex: 55,
                    padding: 6
                  }}
                >
                  {canPlayback && (
                    <button
                      type="button"
                      style={menuItemStyle}
                      onClick={() => {
                        setMoreOpen(false);
                        onOpenMissionPlayback?.(active.id);
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {'\u25B6'} 回放
                    </button>
                  )}
                  <button
                    type="button"
                    style={menuItemStyle}
                    onClick={() => {
                      setMoreOpen(false);
                      onExportMissionKml?.(active);
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    导出 KML
                  </button>
                  <button
                    type="button"
                    style={menuItemStyle}
                    onClick={() => {
                      setMoreOpen(false);
                      setTaskSettingsOpen(true);
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    任务设置
                  </button>
                </div>
              )}
              {taskSettingsOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    width: 260,
                    background: '#fff',
                    border: '1px solid #c5d8f5',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(13,71,161,0.18)',
                    zIndex: 56,
                    padding: 12
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0d47a1', marginBottom: 8 }}>
                    任务设置
                  </div>
                  <div style={{ fontSize: 12, color: '#607d8b', lineHeight: 1.5, marginBottom: 10 }}>
                    更多任务级选项后续补充。当前可开关「任务记录轨迹」（与显示设置同步）。
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                    <span>任务记录轨迹</span>
                    <input
                      type="checkbox"
                      checked={active.recordTracks === true}
                        disabled={!canRecordTracks}
                        onChange={(e) => canRecordTracks && onToggleMissionRecordTracks?.(active.id, e.target.checked)}
                    />
                  </label>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* 状态行：执行中 · 参与 · 在线 */}
      {active ? (
        <div className="mission-task-participation" style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
          <span style={{ color: '#16a34a', fontWeight: 700 }}>执行中</span>
          <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
          <span>{resolvedParticipant}人参与</span>
          <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span>
          <span>{resolvedOnline} 在线</span>
        </div>
      ) : null}

      {/* 汇总条（截图：浅灰一行，数字加粗并带语义色） */}
      {active ? (
        <div
          className="mission-task-summary" title="上报策略汇总；加强/待生效/省电待单兵字段联调，离线按在线判定"
          style={{
            marginTop: 8,
            marginBottom: 4,
            padding: '7px 10px',
            background: '#f3f4f6',
            borderRadius: 8,
            fontSize: 12,
            color: '#4b5563',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            alignItems: 'center',
            lineHeight: 1.4
          }}
        >
          {[
            { label: '实时加强', value: freqTallies.boost, color: '#15803d', tip: '占位/派生：待单兵高频模式字段联调' },
            { label: '等待生效', value: freqTallies.pending, color: '#c2410c', tip: '占位：指挥申请高频待队员确认' },
            { label: '省电', value: freqTallies.eco, color: '#1d4ed8', tip: '占位：待单兵省电模式字段' },
            { label: '离线', value: freqTallies.offline, color: '#6b7280', tip: '按当前在线判定统计' }
          ].map((item, idx, arr) => (
            <span key={item.label} title={item.tip} style={{ whiteSpace: 'nowrap' }}>
              <span>{item.label} </span>
              <span style={{ fontWeight: 800, color: item.color }}>{item.value}</span>
              <span> 人</span>
              {idx < arr.length - 1 ? <span style={{ margin: '0 6px', color: '#d1d5db' }}>·</span> : null}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
